import axios from "axios";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const apiUrlBebidas = `${(process.env.REACT_APP_API_PLATOS || 'http://localhost:8085/api/platos/').replace(/\/+$/, '')}/../bebidas`;

const AbmBebidas = () => {
    const [bebidas, setBebidas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editando, setEditando] = useState(null);
    const [formData, setFormData] = useState({
        tipo: 'gaseosa',
        marca: '',
        sabor: '',
        precio: ''
    });
    const navigate = useNavigate();

    useEffect(() => {
        getBebidas();
    }, []);

    const getBebidas = async () => {
        try {
            setLoading(true);
            const response = await axios.get(apiUrlBebidas);
            setBebidas(response.data);
        } catch (error) {
            console.error("Error al obtener bebidas:", error);
            alert("Error al cargar las bebidas");
        } finally {
            setLoading(false);
        }
    };

    const cambiarEstado = async (id, nuevoEstado) => {
        try {
            await axios.put(`${apiUrlBebidas}/${id}`, { activo: nuevoEstado });
        } catch (error) {
            console.error("Error al cambiar estado:", error);
            alert("Error al cambiar el estado de la bebida");
            getBebidas();
        }
    };

    const onDragEnd = (result) => {
        const { source, destination, draggableId } = result;

        if (!destination ||
            (source.droppableId === destination.droppableId && source.index === destination.index)) {
            return;
        }

        const nuevoEstado = destination.droppableId === 'activos';
        const bebidaId = parseInt(draggableId);

        setBebidas(prevBebidas =>
            prevBebidas.map(bebida =>
                bebida.id_bebida === bebidaId
                    ? { ...bebida, activo: nuevoEstado }
                    : bebida
            )
        );

        cambiarEstado(bebidaId, nuevoEstado);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.marca.trim() || !formData.sabor.trim()) {
            alert("Marca y sabor son obligatorios");
            return;
        }

        try {
            const payload = {
                tipo: formData.tipo,
                marca: formData.marca.trim(),
                sabor: formData.sabor.trim(),
                precio: formData.precio ? parseFloat(formData.precio) : null
            };

            if (editando) {
                await axios.put(`${apiUrlBebidas}/${editando}`, payload);
                alert("✅ Bebida actualizada correctamente");
            } else {
                await axios.post(apiUrlBebidas, payload);
                alert("✅ Bebida creada correctamente");
            }

            setShowModal(false);
            setEditando(null);
            setFormData({ tipo: 'gaseosa', marca: '', sabor: '', precio: '' });
            getBebidas();
        } catch (error) {
            console.error("Error al guardar bebida:", error);
            const mensaje = error.response?.data?.message || "Error al guardar la bebida";
            alert(`❌ ${mensaje}`);
        }
    };

    const handleEditar = (bebida) => {
        setEditando(bebida.id_bebida);
        setFormData({
            tipo: bebida.tipo,
            marca: bebida.marca,
            sabor: bebida.sabor,
            precio: bebida.precio || ''
        });
        setShowModal(true);
    };

    const handleEliminarPermanente = async (id, nombre) => {
        if (!window.confirm(`¿Seguro que deseas eliminar permanentemente "${nombre}"? Esta acción no se puede deshacer.`)) {
            return;
        }

        try {
            await axios.delete(`${apiUrlBebidas}/${id}/permanente`);
            alert("✅ Bebida eliminada permanentemente");
            getBebidas();
        } catch (error) {
            console.error("Error al eliminar bebida:", error);
            alert("❌ Error al eliminar la bebida");
        }
    };

    const bebidasActivas = bebidas.filter(b => b.activo);
    const bebidasInactivas = bebidas.filter(b => !b.activo);

    const getEmojiTipo = (tipo) => tipo === 'gaseosa' ? '🥤' : '💧';

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 py-8 px-4">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border-b-4 border-blue-500">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div className="flex items-center gap-4">
                            <div className="bg-gradient-to-br from-blue-500 to-cyan-600 p-4 rounded-xl shadow-lg">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                </svg>
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-800">Gestión de Bebidas</h1>
                                <p className="text-gray-600 mt-1">Administra las bebidas disponibles para los menús</p>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setEditando(null);
                                    setFormData({ tipo: 'gaseosa', marca: '', sabor: '', precio: '' });
                                    setShowModal(true);
                                }}
                                className="px-6 py-3 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white font-bold shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Nueva Bebida
                            </button>
                            <button
                                onClick={() => navigate('/admin/carga-menu-semanal')}
                                className="px-6 py-3 rounded-lg bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white font-bold shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                Volver
                            </button>
                        </div>
                    </div>

                    <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-4 border-2 border-blue-200 mt-4">
                        <p className="font-bold text-gray-800 mb-1 flex items-center gap-2">
                            <span>💡</span> Tip: Arrastra y suelta
                        </p>
                        <p className="text-gray-700 text-sm">Arrastra las bebidas entre las columnas "Activas" e "Inactivas" para cambiar su disponibilidad.</p>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-12">
                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                        <p className="text-gray-600 mt-4">Cargando bebidas...</p>
                    </div>
                ) : (
                    <DragDropContext onDragEnd={onDragEnd}>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Columna ACTIVAS */}
                            <div className="bg-white rounded-xl shadow-lg border-2 border-green-300">
                                <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-4 rounded-t-xl">
                                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                        ✅ Activas ({bebidasActivas.length})
                                    </h3>
                                    <p className="text-green-50 text-sm mt-1">
                                        Bebidas disponibles para ofrecer
                                    </p>
                                </div>
                                <Droppable droppableId="activos">
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            className={`p-4 min-h-[400px] transition-colors ${snapshot.isDraggingOver ? 'bg-green-50' : 'bg-white'}`}
                                        >
                                            {bebidasActivas.length === 0 ? (
                                                <div className="text-center text-gray-500 py-12">
                                                    <p className="text-lg font-medium">No hay bebidas activas</p>
                                                    <p className="text-sm mt-2">Arrastra desde Inactivas o crea una nueva</p>
                                                </div>
                                            ) : (
                                                <div className="space-y-3">
                                                    {bebidasActivas.map((bebida, index) => (
                                                        <Draggable
                                                            key={bebida.id_bebida}
                                                            draggableId={String(bebida.id_bebida)}
                                                            index={index}
                                                        >
                                                            {(provided, snapshot) => (
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                    className={`bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 shadow-md hover:shadow-xl transition-all border-2 border-green-300 ${snapshot.isDragging ? 'rotate-2 shadow-2xl scale-105 ring-4 ring-green-400/50' : ''}`}
                                                                    style={provided.draggableProps.style}
                                                                >
                                                                    <div className="flex justify-between items-start mb-2">
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-2xl text-gray-400 cursor-grab">⋮⋮</span>
                                                                            <h4 className="text-gray-800 font-bold text-lg flex items-center gap-2">
                                                                                {getEmojiTipo(bebida.tipo)} {bebida.marca}
                                                                            </h4>
                                                                        </div>
                                                                        <button
                                                                            onClick={() => handleEditar(bebida)}
                                                                            className="px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold shadow-md hover:shadow-lg transition-all"
                                                                        >
                                                                            ✏️ Editar
                                                                        </button>
                                                                    </div>
                                                                    <p className="text-gray-700 text-sm">
                                                                        <span className="font-semibold">Sabor:</span> {bebida.sabor}
                                                                    </p>
                                                                    <p className="text-gray-700 text-sm capitalize">
                                                                        <span className="font-semibold">Tipo:</span> {bebida.tipo}
                                                                    </p>
                                                                    {bebida.precio && (
                                                                        <p className="text-green-600 text-sm font-bold mt-1">
                                                                            ${parseFloat(bebida.precio).toFixed(2)}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    ))}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Droppable>
                            </div>

                            {/* Columna INACTIVAS */}
                            <div className="bg-white rounded-xl shadow-lg border-2 border-red-300">
                                <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4 rounded-t-xl">
                                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                        ❌ Inactivas ({bebidasInactivas.length})
                                    </h3>
                                    <p className="text-red-50 text-sm mt-1">
                                        Bebidas no disponibles
                                    </p>
                                </div>
                                <Droppable droppableId="inactivos">
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            className={`p-4 min-h-[400px] transition-colors ${snapshot.isDraggingOver ? 'bg-red-50' : 'bg-white'}`}
                                        >
                                            {bebidasInactivas.length === 0 ? (
                                                <div className="text-center text-gray-500 py-12">
                                                    <p className="text-lg font-medium">No hay bebidas inactivas</p>
                                                </div>
                                            ) : (
                                                <div className="space-y-3">
                                                    {bebidasInactivas.map((bebida, index) => (
                                                        <Draggable
                                                            key={bebida.id_bebida}
                                                            draggableId={String(bebida.id_bebida)}
                                                            index={index}
                                                        >
                                                            {(provided, snapshot) => (
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                    className={`bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl p-4 shadow-md hover:shadow-xl transition-all border-2 border-red-300 opacity-70 ${snapshot.isDragging ? 'rotate-2 shadow-2xl scale-105 ring-4 ring-red-400/50' : ''}`}
                                                                    style={provided.draggableProps.style}
                                                                >
                                                                    <div className="flex justify-between items-start mb-2">
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-2xl text-gray-400 cursor-grab">⋮⋮</span>
                                                                            <h4 className="text-gray-700 font-bold text-lg flex items-center gap-2">
                                                                                {getEmojiTipo(bebida.tipo)} {bebida.marca}
                                                                            </h4>
                                                                        </div>
                                                                        <div className="flex gap-2">
                                                                            <button
                                                                                onClick={() => handleEditar(bebida)}
                                                                                className="px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold shadow-md hover:shadow-lg transition-all"
                                                                            >
                                                                                ✏️
                                                                            </button>
                                                                            <button
                                                                                onClick={() => handleEliminarPermanente(bebida.id_bebida, `${bebida.marca} - ${bebida.sabor}`)}
                                                                                className="px-3 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-xs font-semibold shadow-md hover:shadow-lg transition-all"
                                                                            >
                                                                                🗑️
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                    <p className="text-gray-600 text-sm">
                                                                        <span className="font-semibold">Sabor:</span> {bebida.sabor}
                                                                    </p>
                                                                    <p className="text-gray-600 text-sm capitalize">
                                                                        <span className="font-semibold">Tipo:</span> {bebida.tipo}
                                                                    </p>
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    ))}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </Droppable>
                            </div>
                        </div>
                    </DragDropContext>
                )}

                {/* Modal Crear/Editar */}
                {showModal && (
                    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full border-4 border-blue-500">
                            <div className="bg-gradient-to-r from-blue-500 to-cyan-600 px-6 py-4 rounded-t-2xl">
                                <h3 className="text-xl font-bold text-white">
                                    {editando ? '✏️ Editar Bebida' : '➕ Nueva Bebida'}
                                </h3>
                            </div>
                            <form onSubmit={handleSubmit} className="p-6 space-y-4">
                                <div>
                                    <label className="block text-gray-700 font-bold mb-2">Tipo</label>
                                    <select
                                        value={formData.tipo}
                                        onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                                        className="w-full bg-white text-gray-800 border-2 border-gray-300 p-3 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                                        required
                                    >
                                        <option value="gaseosa">🥤 Gaseosa</option>
                                        <option value="agua">💧 Agua</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-bold mb-2">Marca *</label>
                                    <input
                                        type="text"
                                        value={formData.marca}
                                        onChange={(e) => setFormData({ ...formData, marca: e.target.value })}
                                        className="w-full bg-white text-gray-800 border-2 border-gray-300 p-3 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                                        placeholder="Ej: Coca Cola, Pepsi, Villavicencio"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-bold mb-2">Sabor *</label>
                                    <input
                                        type="text"
                                        value={formData.sabor}
                                        onChange={(e) => setFormData({ ...formData, sabor: e.target.value })}
                                        className="w-full bg-white text-gray-800 border-2 border-gray-300 p-3 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                                        placeholder="Ej: Cola, Naranja, Sin Gas, Con Gas"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-bold mb-2">Precio (opcional)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={formData.precio}
                                        onChange={(e) => setFormData({ ...formData, precio: e.target.value })}
                                        className="w-full bg-white text-gray-800 border-2 border-gray-300 p-3 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition"
                                        placeholder="0.00"
                                    />
                                </div>
                                <div className="flex gap-3 pt-4">
                                    <button
                                        type="submit"
                                        className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white font-bold py-3 rounded-lg transition-all shadow-lg"
                                    >
                                        {editando ? 'Actualizar' : 'Crear'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowModal(false);
                                            setEditando(null);
                                            setFormData({ tipo: 'gaseosa', marca: '', sabor: '', precio: '' });
                                        }}
                                        className="flex-1 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white font-bold py-3 rounded-lg transition-all shadow-lg"
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AbmBebidas;
